import React, { useState } from 'react';
import { Modal, Upload, Button, message, Alert, Space, Typography } from 'antd';
import AdaptiveTable from './AdaptiveTable';
import { UploadOutlined } from '@ant-design/icons';
import {
  productApi,
  type ProductDTO,
  type ProductImportPreviewDTO,
} from '../api/kfc';

interface ProductImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const productLabel = (p: ProductDTO) =>
  `${p.hscode || ''} ${p.nameZN || ''} ${p.nameEN || ''}`.trim();

const ProductImportModal: React.FC<ProductImportModalProps> = ({ open, onClose, onSuccess }) => {
  const [preview, setPreview] = useState<ProductImportPreviewDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const reset = () => {
    setPreview(null);
    setLoading(false);
    setConfirming(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleUpload = (file: File) => {
    setLoading(true);
    productApi
      .importPreview(file)
      .then((res) => {
        setPreview(res.data);
        const s = res.data.summary;
        message.info(`解析完成：新增 ${s.create}，修改 ${s.update}，无变化 ${s.unchanged}，错误 ${s.error}`);
      })
      .catch((e) => message.error(e.response?.data?.message || '解析失败'))
      .finally(() => setLoading(false));
    return false;
  };

  const handleConfirm = () => {
    if (!preview) return;
    const toCreate = preview.toCreate || [];
    const toUpdate = (preview.toUpdate || []).map((item) => item.after);
    if (!toCreate.length && !toUpdate.length) {
      message.info('没有需要导入的变更');
      return;
    }
    setConfirming(true);
    productApi
      .importConfirm({ toCreate, toUpdate })
      .then((res) => {
        message.success(`导入完成：新增 ${res.data.created}，更新 ${res.data.updated}`);
        handleClose();
        onSuccess();
      })
      .catch((e) => message.error(e.response?.data?.message || '导入失败'))
      .finally(() => setConfirming(false));
  };

  return (
    <Modal
      title="导入产品"
      open={open}
      onCancel={handleClose}
      width={900}
      footer={
        preview ? (
          <Space>
            <Button onClick={() => setPreview(null)}>重新选择文件</Button>
            <Button onClick={handleClose}>取消</Button>
            <Button
              type="primary"
              loading={confirming}
              disabled={!preview.toCreate?.length && !preview.toUpdate?.length}
              onClick={handleConfirm}
            >
              确认导入
            </Button>
          </Space>
        ) : (
          <Button onClick={handleClose}>关闭</Button>
        )
      }
    >
      {!preview ? (
        <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleUpload}>
          <Button icon={<UploadOutlined />} loading={loading}>
            选择 Excel 文件
          </Button>
        </Upload>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              <Typography.Text>
                新增 {preview.summary.create} 条，修改 {preview.summary.update} 条，无变化{' '}
                {preview.summary.unchanged} 条
                {preview.summary.error > 0 ? `，错误 ${preview.summary.error} 条` : ''}
              </Typography.Text>
            }
          />
          {preview.errors?.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="以下行无法导入"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {preview.errors.map((e) => (
                    <li key={e.row}>
                      第 {e.row} 行：{e.message}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
          {preview.toCreate?.length > 0 && (
            <>
              <Typography.Title level={5}>新增（{preview.toCreate.length}）</Typography.Title>
              <AdaptiveTable
                size="small"
                rowKey={(r) => `${r.nameEN}-${r.hscode}`}
                dataSource={preview.toCreate}
                pagination={{ pageSize: 5 }}
                scroll={{ x: 800 }}
                columns={[
                  { title: '海关编码', dataIndex: 'hscode', width: 110 },
                  { title: '名称(中)', dataIndex: 'nameZN', width: 100, ellipsis: true },
                  { title: '名称(英)', dataIndex: 'nameEN', width: 120, ellipsis: true },
                  { title: '品牌', dataIndex: 'brand', width: 80, ellipsis: true },
                  { title: '型号', dataIndex: 'model', width: 100, ellipsis: true },
                ]}
                style={{ marginBottom: 16 }}
              />
            </>
          )}
          {preview.toUpdate?.length > 0 && (
            <>
              <Typography.Title level={5}>修改（{preview.toUpdate.length}）</Typography.Title>
              <AdaptiveTable
                size="small"
                rowKey={(r) => `${r.after.nameEN}-${r.after.hscode}`}
                dataSource={preview.toUpdate}
                pagination={{ pageSize: 5 }}
                scroll={{ x: 900 }}
                columns={[
                  {
                    title: '产品',
                    key: 'label',
                    width: 200,
                    ellipsis: true,
                    render: (_, r) => productLabel(r.after),
                  },
                  {
                    title: '变更字段',
                    key: 'diff',
                    render: (_, r) => {
                      const fields: Array<keyof ProductDTO> = [
                        'nameZN',
                        'brand',
                        'model',
                        'containsBattery',
                        'containsMagnet',
                      ];
                      const changed = fields.filter((f) => (r.before[f] || '') !== (r.after[f] || ''));
                      const usageChanged =
                        JSON.stringify(r.before.usage || {}) !== JSON.stringify(r.after.usage || {});
                      const materialChanged =
                        JSON.stringify(r.before.material || {}) !== JSON.stringify(r.after.material || {});
                      const labels = [...changed];
                      if (usageChanged) labels.push('usage' as keyof ProductDTO);
                      if (materialChanged) labels.push('material' as keyof ProductDTO);
                      return labels.length ? labels.join('、') : '-';
                    },
                  },
                ]}
              />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default ProductImportModal;
